import { AlertCircleIcon } from 'lucide-react-native';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { Input, InputField } from '@/components/ui/input';
import { getSurveyProseKeyboardProps } from './surveyKeyboard';
import { getSurveyOtherFieldName, type SurveyElement } from '../../model/surveySchema';

interface SurveyOtherTextInputProps {
  element: SurveyElement;
  control: Control<FieldValues>;
  errorMessage?: string;
}

/**
 * The free-text field that accompanies a selected Other option.
 *
 * Shared across the three choice renderers so the field name, the label, and
 * the required-once-selected behavior cannot drift between them. The name is
 * SurveyJS's own convention rather than ours, and the API validates with
 * survey-core, so a renderer that bound a differently-named field would submit
 * text the server discards.
 *
 * Rendered only while Other is selected. It carries its own `FormControl` so
 * the error belongs to this field rather than to the choice question above it —
 * the question itself is answered, and it is the missing text that blocks.
 */
export function SurveyOtherTextInput({
  element,
  control,
  errorMessage,
}: SurveyOtherTextInputProps) {
  const fieldName = getSurveyOtherFieldName(element.name);

  const label = 'Please specify';

  const labelId = `${fieldName}-label`;

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { onChange, onBlur, value } }) => (
        <FormControl isInvalid={!!errorMessage} isRequired className="gap-2 pl-7">
          <FormControlLabel className="items-start">
            <FormControlLabelText nativeID={labelId}>{label}</FormControlLabelText>
          </FormControlLabel>

          <Input>
            <InputField
              testID={fieldName}
              {...getSurveyProseKeyboardProps()}
              value={typeof value === 'string' ? value : ''}
              onChangeText={onChange}
              onBlur={onBlur}
              aria-labelledby={labelId}
              accessibilityLabelledBy={labelId}
              aria-label={label}
              accessibilityLabel={label}
            />
          </Input>

          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} />
            <FormControlErrorText>{errorMessage}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
